import {
  AfterViewInit,
  Component,
  HostBinding,
  LOCALE_ID,
  OnInit,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AbEntrySearchService,
  LeadSearchService,
} from '@maximizer/core/global-search/data-access';
import { UserService } from '@maximizer/core/shared/data-access';
import {
  AbEntrySearch,
  GroupedListItem,
  LeadSearch,
} from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StringValidator } from '@maximizer/core/shared/ui/validators';
import {
  AbEntryService,
  AppointmentOccurrenceLimitError,
  AppointmentSeriesError,
  AppointmentService,
  LeadService,
  NavigationService,
  OutlookSessionService,
  OutlookStore,
  seriesProgressParams,
  RECURRENCE_DATE_FORMAT,
  TrackerService,
  appointmentAttendeeIcon,
  capRecurrenceEndsOn,
  clampRecurrenceEndsOn,
  clientNow,
  describeRecurrence,
  describeRecurrenceTruncation,
  isRecurrenceTruncated,
  nextHalfHour,
  nullIfBlank,
  recurrenceText,
  resolveAppointmentLocation,
  spansMultipleCalendarDays,
  toRichTextFragment,
} from '@maximizer/outlook/shared/data-access';
import {
  AppointmentAttendeeGroup,
  AppointmentForm,
  AppointmentModuleConfiguration,
  AppointmentPrefill,
  AppointmentRecurrence,
  EmailEntryType,
  EntryType,
  EventAction,
  EventJourney,
  EventModule,
  OutlookAbEntrySearch,
  OutlookLeadSearch,
  SaveAppointmentResult,
  SeriesSaveProgress,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_EDITOR } from '@progress/kendo-angular-editor';
import {
  KENDO_DROPDOWNS,
  RemoveTagEvent,
} from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLBAR } from '@progress/kendo-angular-toolbar';
import { catchError, finalize, forkJoin, Observable, of } from 'rxjs';
import { AppointmentRepeatDialogComponent } from '../repeat-dialog/repeat-dialog.component';

const SUBJECT_MAX_LENGTH = 1024;
const DEFAULT_DURATION_MINUTES = 30;
const MILLISECONDS_PER_MINUTE = 60000;
const ATTENDEE_SEARCH_MIN_LENGTH = 3;
const ATTENDEE_SEARCH_DELAY_MS = 1000;
const DESCRIPTION_EDITOR_HEIGHT_PX = 180;

const DESCRIPTION_PASTE_CLEANUP = {
  convertMsLists: true,
  removeHtmlComments: true,
  stripTags: ['img', 'script'],
  removeMsClasses: true,
  removeMsStyles: true,
  removeInvalidHTML: true,
};

@Component({
  selector: 'maximizer-add-appointment',
  templateUrl: './add-appointment.component.html',
  imports: [
    AppointmentRepeatDialogComponent,
    FormsModule,
    KENDO_BUTTONS,
    KENDO_DATEINPUTS,
    KENDO_DROPDOWNS,
    KENDO_EDITOR,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLBAR,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddAppointmentComponent implements OnInit, AfterViewInit {
  @HostBinding('class.max-outlook') hostClass = true;

  readonly notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  readonly type = input<EmailEntryType | null>(null);
  readonly id = input<string | null>(null);
  readonly embedded = input(false);
  readonly prefill = input<AppointmentPrefill | null>(null);

  readonly saved = output<SaveAppointmentResult>();
  readonly cancelled = output<void>();

  // Embedded display only
  readonly partiallySaved = output<AppointmentSeriesError>();
  readonly savingChange = output<boolean>();

  private readonly entryKey = computed(() => {
    const id = this.id();
    return id ? decodeURI(id) : null;
  });
  private readonly leadKey = computed(() =>
    this.type() === EntryType.Lead ? this.entryKey() : null,
  );
  private readonly abEntryKey = computed(() =>
    this.type() === EntryType.AbEntry ? this.entryKey() : null,
  );

  readonly recurrence = signal<AppointmentRecurrence | null>(null);
  readonly repeatDialogOpen = signal(false);
  readonly multiDayPrefill = signal(false);
  readonly saveProgress = signal<SeriesSaveProgress | null>(null);

  readonly saveProgressParams = computed(() => {
    const progress = this.saveProgress();
    return progress ? seriesProgressParams(progress) : null;
  });

  readonly recurrenceSummary = computed(() => {
    const recurrence = this.recurrence();
    return recurrence
      ? describeRecurrence(recurrence, this.recurrenceText)
      : '';
  });

  readonly recurrenceTruncationNote = computed(() => {
    const recurrence = this.recurrence();
    return recurrence
      ? describeRecurrenceTruncation(recurrence, this.recurrenceText)
      : null;
  });

  private readonly localeId = inject(LOCALE_ID);
  private readonly recurrenceText = recurrenceText(
    (key, params) => this.translate.instant(key, params),
    this.localeId,
  );

  form: FormGroupWrapper<AppointmentForm>;
  configuration: AppointmentModuleConfiguration;
  filterableAttendeeList: GroupedListItem<string>[] = [];
  autocompleteLoading = false;
  previousAutocompleteInput = '';
  dateFormat = RECURRENCE_DATE_FORMAT;
  subjectMaxLength = SUBJECT_MAX_LENGTH;
  descriptionEditorHeight = DESCRIPTION_EDITOR_HEIGHT_PX;
  descriptionPasteCleanup = DESCRIPTION_PASTE_CLEANUP;
  loading = true;

  currentUser: GroupedListItem<string> = {
    id: '',
    name: '',
    isDisabled: true,
    group: 'user',
  };

  constructor(
    private readonly appointmentService: AppointmentService,
    private readonly session: OutlookSessionService,
    private readonly userService: UserService,
    private readonly abEntrySearchService: AbEntrySearchService,
    private readonly leadSearchService: LeadSearchService,
    private readonly leadService: LeadService,
    private readonly abEntryService: AbEntryService,
    private readonly navigationService: NavigationService,
    private readonly translate: TranslateService,
    private readonly tracker: TrackerService,
    public view: ViewContainerRef,
    public outlookStore: OutlookStore,
  ) {
    this.configuration = {
      options: {
        categories: [],
        results: [],
        locations: [],
      },
      users: [],
    };

    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.applyPrefill();
    this.loadData();
  }

  ngAfterViewInit(): void {
    if (this.multiDayPrefill()) {
      this.notification().showMessage(
        'warning',
        'outlook.appointment.multiDayUnsupported',
        true,
      );
    } else if (this.prefill()?.hasUnsupportedRecurrence) {
      this.notification().showMessage(
        'warning',
        'outlook.appointment.recurrence.unsupported',
        true,
      );
    }
  }

  openRepeatDialog(): void {
    this.repeatDialogOpen.set(true);
    this.tracker.trackEvent({
      module: EventModule.Appointment,
      journey: EventJourney.Repeat,
      action: EventAction.Opened,
    });
  }

  onRepeatSaved(recurrence: AppointmentRecurrence): void {
    this.repeatDialogOpen.set(false);
    this.setRecurrence(recurrence);
    this.form.patch({ date: recurrence.startsOn });
    this.tracker.trackEvent({
      module: EventModule.Appointment,
      journey: EventJourney.Repeat,
      action: EventAction.Completed,
      metadata: {
        repeat: recurrence.repeat,
        skipWeekends: recurrence.skipWeekends,
      },
    });
  }

  onRepeatCancelled(): void {
    this.repeatDialogOpen.set(false);
  }

  clearRecurrence(): void {
    this.setRecurrence(null);
  }

  onDateChange(): void {
    const recurrence = this.recurrence();
    const startsOn = this.form.controls.date.value;
    if (!recurrence || !startsOn) return;

    const withinRange = {
      ...recurrence,
      startsOn,
      endsOn: clampRecurrenceEndsOn(startsOn, recurrence.endsOn),
    };
    this.setRecurrence({
      ...withinRange,
      endsOn: capRecurrenceEndsOn(withinRange),
    });
  }

  onStartChange(): void {
    this.form.patch({
      endTime: this.form.controls.startTime.value,
    });
  }

  onAllDayChange(isAllDay: boolean): void {
    const { startTime, endTime } = this.form.controls;
    if (isAllDay) {
      startTime.control.disable();
      endTime.control.disable();
    } else {
      startTime.control.enable();
      endTime.control.enable();
    }
  }

  disableAttendees(itemArgs: {
    dataItem: GroupedListItem<string>;
    index: number;
  }): boolean {
    return itemArgs.dataItem.isDisabled;
  }

  handleRemoveAttendees(removedItem: RemoveTagEvent): void {
    const removedIndex = this.filterableAttendeeList.indexOf(
      removedItem.dataItem,
    );
    this.filterableAttendeeList.splice(removedIndex, 1);
  }

  resetAttendeesToSelected(): void {
    this.filterableAttendeeList = this.form.value.attendees ?? [];
  }

  handleAttendeesAutoComplete(inputText: string): void {
    if (this.previousAutocompleteInput != inputText) {
      this.previousAutocompleteInput = inputText;
    }
    if (inputText.length < ATTENDEE_SEARCH_MIN_LENGTH) {
      this.autocompleteLoading = false;
      return;
    }
    this.autocompleteLoading = true;

    setTimeout(() => {
      if (this.previousAutocompleteInput == inputText) {
        forkJoin({
          abentries: this.abEntrySearchService.search(inputText, true),
          leads: this.leadSearchService.search(inputText, true),
        })
          .pipe(
            catchError(async () => {
              this.notification().show('load', 'error');
            }),
            finalize(() => (this.autocompleteLoading = false)),
          )
          .subscribe((data) =>
            this.filterAutocompleteLoadedData(
              inputText,
              data?.abentries,
              data?.leads,
            ),
          );
      }
    }, ATTENDEE_SEARCH_DELAY_MS);
  }

  save(): void {
    this.form.patch({
      creator: this.currentUser.id,
      category: nullIfBlank(this.form.value.category),
      result: nullIfBlank(this.form.value.result),
      description: toRichTextFragment(this.form.value.description),
    });

    const validation = this.form.validate();
    if (!validation.valid) return;

    // Resolved outside the form: the combobox control must keep holding the selection or typed text.
    const location = resolveAppointmentLocation(
      this.form.value.location,
      this.configuration.options.locations,
    );

    this.loading = true;
    this.savingChange.emit(true);
    this.appointmentService
      .create({ ...this.form.value, location }, (progress) =>
        this.saveProgress.set(progress),
      )
      .pipe(
        catchError((error: unknown) => {
          this.showCreateError(error);
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
          this.saveProgress.set(null);
          this.savingChange.emit(false);
        }),
      )
      .subscribe((appointmentKey) => {
        if (!appointmentKey) return;
        if (this.embedded()) {
          this.saved.emit(this.toSaveResult(appointmentKey));
          return;
        }
        this.notification().show('create', 'success');
        this.navigationService.navigateBack();
      });
  }

  private toSaveResult(appointmentKey: string): SaveAppointmentResult {
    const recurrence = this.recurrence();
    return {
      appointmentKey,
      hasUnsupportedRecurrence:
        !recurrence && !!this.prefill()?.hasUnsupportedRecurrence,
      savedThrough:
        recurrence && isRecurrenceTruncated(recurrence)
          ? recurrence.endsOn
          : null,
    };
  }

  private showCreateError(error: unknown): void {
    if (error instanceof AppointmentSeriesError && this.embedded()) {
      this.partiallySaved.emit(error);
      return;
    }
    if (error instanceof AppointmentSeriesError) {
      this.notification().showMessage(
        'error',
        this.translate.instant(
          'outlook.appointment.recurrence.partialError',
          seriesProgressParams(error.progress),
        ),
      );
      return;
    }
    if (error instanceof AppointmentOccurrenceLimitError) {
      this.notification().showMessage(
        'error',
        this.translate.instant(
          'outlook.appointment.recurrence.tooManyOccurrences',
          { max: error.maxOccurrences },
        ),
      );
      return;
    }
    this.notification().show('create', 'error');
  }

  cancel(): void {
    if (this.embedded()) {
      this.cancelled.emit();
      return;
    }
    this.navigationService.navigateBack();
  }

  private buildForm(): FormGroupWrapper<AppointmentForm> {
    const dateStart: Date = nextHalfHour(clientNow());
    const dateEnd: Date = new Date(
      dateStart.getTime() + DEFAULT_DURATION_MINUTES * MILLISECONDS_PER_MINUTE,
    );

    return FormWrapperBuilder.group<AppointmentForm>({
      key: null,
      creator: this.currentUser.id,
      subject: [
        null,
        {
          validators: Validators.compose([
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(SUBJECT_MAX_LENGTH),
            StringValidator.NotEmpty(),
          ]),
        },
      ],
      date: [dateStart, [Validators.required]],
      startTime: [dateStart, [Validators.required]],
      endTime: [dateEnd, [Validators.required]],
      isAllDay: false,
      isPrivate: false,
      location: null,
      assignedUser: [null, Validators.required],
      attendees: [null, [Validators.required]],
      description: null,
      category: null,
      result: null,
      recurrence: null,
    });
  }

  private setRecurrence(recurrence: AppointmentRecurrence | null): void {
    this.recurrence.set(recurrence);
    this.form.patch({ recurrence });
  }

  private applyPrefill(): void {
    const prefill = this.prefill();
    if (!prefill) return;

    if (prefill.recurrence) {
      this.setRecurrence(prefill.recurrence);
    }
    this.multiDayPrefill.set(
      !!prefill.startTime &&
        !!prefill.endTime &&
        spansMultipleCalendarDays(prefill.startTime, prefill.endTime),
    );

    this.form.patch({
      ...(prefill.subject === null ? {} : { subject: prefill.subject }),
      ...(prefill.description === null
        ? {}
        : { description: prefill.description }),
      ...(prefill.startTime === null
        ? {}
        : { startTime: prefill.startTime, date: prefill.startTime }),
      ...(prefill.endTime === null ? {} : { endTime: prefill.endTime }),
      ...(prefill.isAllDay === null ? {} : { isAllDay: prefill.isAllDay }),
      ...(prefill.isPrivate === null ? {} : { isPrivate: prefill.isPrivate }),
      ...(prefill.location === null ? {} : { location: prefill.location }),
      ...(prefill.attendees === null ? {} : { attendees: prefill.attendees }),
    });

    if (prefill.isAllDay) this.onAllDayChange(true);
  }

  private loadData(): void {
    forkJoin({
      appointmentOptions: this.appointmentService.getFieldOption(),
      users: this.userService.getActiveUsers(),
      currentUser: this.session.getCurrentUser(),
      mainObject: this.loadLeadOrAbEntryService(),
    })
      .pipe(
        catchError(() => {
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((data) => {
        if (!data) return;

        this.currentUser = {
          id: data.currentUser.key,
          name: data.currentUser.name,
          isDisabled: true,
          icon: appointmentAttendeeIcon('user'),
          group: 'user',
        };

        this.loadDefaultAttendees(data.mainObject);

        const { categories, results, locations } = data.appointmentOptions;

        this.configuration = {
          options: {
            categories: categories ?? [],
            results: results ?? [],
            locations: locations ?? [],
          },
          users: data.users ?? [],
        };

        this.form.patch({
          assignedUser: this.currentUser.id,
        });
      });
  }

  private filterAutocompleteLoadedData(
    inputText: string,
    abentries?: AbEntrySearch[],
    leads?: LeadSearch[],
  ): void {
    if (!abentries && !leads) return;
    const attendees: GroupedListItem<string>[] = [];

    this.filterUsersToAttendees(inputText, attendees);
    this.filterAttendees(abentries, attendees, 'abentry');
    this.filterAttendees(leads, attendees, 'lead');

    attendees.sort((first, second) => first.name.localeCompare(second.name));
    this.filterableAttendeeList = attendees;
  }

  private loadLeadOrAbEntryService(): Observable<
    OutlookAbEntrySearch | OutlookLeadSearch | null
  > {
    const leadKey = this.leadKey();
    if (leadKey) return this.leadService.getByKey(leadKey);

    const abEntryKey = this.abEntryKey();
    if (abEntryKey) return this.abEntryService.getByKey(abEntryKey);

    return of(null);
  }

  private loadDefaultAttendees(
    mainObject: OutlookAbEntrySearch | OutlookLeadSearch | null,
  ): void {
    this.filterableAttendeeList = [this.currentUser];

    if (!this.leadKey() && !this.abEntryKey()) {
      const prefilledAttendees = this.prefill()?.attendees ?? [];
      this.filterableAttendeeList.push(...prefilledAttendees);
      this.form.patch({
        attendees: [this.currentUser, ...prefilledAttendees],
      });
      return;
    }

    const attendee: GroupedListItem<string> = {
      id: mainObject?.key ?? '',
      name: mainObject?.name ?? '',
      isDisabled: false,
      group: '',
    };

    if (this.leadKey()) {
      attendee.icon = appointmentAttendeeIcon('lead');
      attendee.group = 'lead';
      this.filterableAttendeeList.push(attendee);
    }
    if (this.abEntryKey()) {
      const abEntry = mainObject as OutlookAbEntrySearch;
      const isCompany = abEntry.type === EntryType.Company;
      if (isCompany) {
        attendee.name = mainObject?.companyName ?? '';
      }
      attendee.icon = appointmentAttendeeIcon('abentry', isCompany);
      attendee.group = 'abentry';
    }
    this.filterableAttendeeList.push(attendee);
    this.form.patch({
      attendees: [this.currentUser, attendee],
    });
  }

  private filterUsersToAttendees(
    inputText: string,
    attendees: GroupedListItem<string>[],
  ): void {
    if (!this.configuration.users) return;
    if ((this.configuration.users?.length ?? 0) === 0) return;

    const matchingUsers = this.configuration.users.filter(
      (user) =>
        user.name.toLowerCase().includes(inputText.toLowerCase()) &&
        user.key != this.currentUser.id,
    );
    matchingUsers.forEach((user) => {
      attendees.push({
        id: user.key ?? '',
        name: user.name,
        icon: appointmentAttendeeIcon('user'),
        isDisabled: false,
        group: 'user',
      });
    });
  }

  private filterAttendees(
    data: AbEntrySearch[] | LeadSearch[] | null | undefined,
    attendees: GroupedListItem<string>[],
    attendeeType: AppointmentAttendeeGroup,
  ) {
    if (data === null || data === undefined) return;
    if (data.length === 0) return;

    data.forEach((entry) => {
      let isCompany = false;
      if (attendeeType === 'abentry') {
        const abEntry = entry as AbEntrySearch;
        entry.name = abEntry.displayValue;
        isCompany = abEntry.type === EntryType.Company;
      }
      attendees.push({
        id: entry.key ?? '',
        name: entry.name ?? '',
        icon: appointmentAttendeeIcon(attendeeType, isCompany),
        isDisabled: false,
        group: attendeeType,
      });
    });
  }
}
